import { isNotDefined } from '@sgme/fp';
import { ServiceLoader } from '@sgme/ui';
import type { SGWTConnectError } from '@sgwt/connect-core';
import {
  type AcrValue,
  SgwtConnect,
  type SgwtConnectHTMLElement,
  SgwtHelpCenter,
  SgwtSplashScreen,
  SgwtWebAnalytics,
  SgwtWidgetContextProvider,
  useSgwtWidgets,
} from '@sgwt/sgwt-widgets-react';
import type { Infrastructure } from '@sgwt/sgwt-widgets-react/utils';
import { type PropsWithChildren, type ReactNode, useEffect, useRef, useState } from 'react';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { startWorker } from '@/api/signalR/startWorker.ts';
import { ExpiredSessionModal } from '@/components/fatality/ExpiredSessionModal.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { userCanAccessToFx } from '@/components/SgwtBootstrap/userCanAccessFx.ts';
import { getConfig } from '@/config/config.ts';
import { getAuthorizationToken, sgwtConnect } from '@/sgwt/sgwtConnect.ts';

/**
 * ##### WARNING #####
 * this component is temporary
 * use in the future the new @sgme/ui
 */

type SgwtBootstrapProps = {
  infrastructure: Infrastructure;

  sgConnect: {
    clientId: string;
    endpoint: string;
    redirectUri?: string;
    scope: string;
    acrValues?: string;

    // by default, when an error occurs, the user is redirected to a shared page like "you don't have the right"
    // to override this behavior, provide the result to display
    onError?: (error: SGWTConnectError) => ReactNode;
  };

  sgAnalytics?: {
    siteId: string;
    url: string;
  };

  sgSplashScreen?: {
    applicationId: string;
  };
};

/**
 * The SG Bootstrap 5 loader and the light / dark theme selection are NOT handled here:
 * see src/utils/theme.ts (initSgBootstrap, updateTheme), bootstrapped by src/initTheme.ts.
 *
 * This component can be used to initialize default services of an application like:
 * - SgConnect
 * - SgAnalytics (user tracker)
 * - SpashScreen
 * - IntlProvider
 * - logger (@sgme/logger)
 * - ErrorBoundary
 *
 * In your application, you have to add
 * - <SgwtAccountCenter />
 * - <SgwtHelpCenter />
 * - <SgwtMiniFooter />
 *
 * In the index.html, don't load SG widgets scripts because they are loaded with <SgwtWidgetContextProvider> from the CDN.
 *
 * But only
 *
 * <script src='https://sgwt-cdn-widgets.sgmarkets.com/widgets/sgwt-widget-polyfills/sgwt-widget-polyfill-webcomponent.js'></script>
 *
 * And write in /public/silent_renew.html
 * <!DOCTYPE html>
 * <html lang="en">
 * <head>
 *   <meta charset="utf-8" />
 *   <meta content="ie=edge" http-equiv="X-UA-Compatible" />
 *   <meta content="width=device-width, initial-scale=1" name="viewport" />
 *   <title>SG Markets LMAFOREX</title>
 *   <script src="/config.js"></script>
 *   <script src="https://shared.sgmarkets.com/sgwt/connect-core/latest/bundle.min.js"></script>
 *   <script>
 *     const sgwtConnect = setupSGWTConnect(window.sgmeConfiguration.sgconnect);
 *   </script>
 * </head>
 * <body></body>
 * </html>
 *
 * @see https://sgithub.fr.world.socgen/SG-Web-Toolkit/sgwt-widgets-react
 *
 * @example
 * <SgwtBoostrap>
 *   <App />
 * <SgwtBoostrap>
 *
 * // inside App.tsx
 * const App = () => {
 *   const { sgwtWebAnalytics  } = useSgwtWidget()
 *   const logger = useLogger()
 *
 *   return <div />
 * }
 */

type SGWTConnectGlobalState = 'initializing' | 'ready' | 'definitivelyDisconnected';

export const SgwtBootstrap = (props: PropsWithChildren<SgwtBootstrapProps>) => {
  const { infrastructure, sgConnect, sgAnalytics, sgSplashScreen, children } = props;
  const [globalState, setGlobalState] = useState<SGWTConnectGlobalState>('initializing');

  return (
    <SgwtWidgetContextProvider infrastructure={infrastructure}>
      <SgwtConnect
        clientId={sgConnect.clientId}
        authorizationEndpoint={sgConnect.endpoint}
        redirectUri={sgConnect.redirectUri}
        scope={sgConnect.scope}
        acrValues={sgConnect.acrValues as AcrValue | undefined}
        onWidgetReady={(_: SgwtConnectHTMLElement) => {
          if (globalState === 'ready') {
            logger.logWarning('onWidgetReady called twice');
            return;
          }

          sgwtConnect.on('authorizationDiscarded', () => {
            logger.logWarning('SGWT Connect: authorization discarded');
            setGlobalState('definitivelyDisconnected');
          });

          sgwtConnect.on('authorizationExpired', () => {
            logger.logWarning('SGWT Connect: authorization expired');
            setGlobalState('definitivelyDisconnected');
          });

          let nbRenewAuthorizationAttempts = 0; // can't be in component state (scope issue)
          sgwtConnect.on('renewAuthorizationError', (error: any) => {
            logger.logError(`SGWT Connect: renew authorization error ${error}`);
            if (nbRenewAuthorizationAttempts >= 3) {
              logger.logWarning('Too many renew authorization attempts, definitively disconnecting user.');
              setGlobalState('definitivelyDisconnected');
            } else {
              logger.logWarning(`Attempting to renew authorization No. ${nbRenewAuthorizationAttempts + 1}...`);
              nbRenewAuthorizationAttempts++;
              sgwtConnect.requestAuthorization();
            }
          });

          sgwtConnect.on('renewAuthorizationSuccess', () => {
            logger.logInformation('SGWT Connect: renew authorization success');
            nbRenewAuthorizationAttempts = 0;
          });

          sgwtConnect.on('OpenIDMetadataFetchError', () => {
            logger.logError('SGWT Connect: Failed to fetch the OpenID Metadata');
          });

          sgwtConnect.on('OpenIDCheckSessionChanged', () => {
            logger.logWarning('SGWT Connect: Open ID check session changed');
          });

          window.SGWTWidgetConfiguration.bus.subscribe(
            'sg-connect.access-token',
            (authorizationHeader: string | undefined) => {
              if (isNotDefined(authorizationHeader)) {
                logger.logError(
                  "received undefined authorization header from SGConnect, can't relay to signalRConnector",
                );
                return;
              }

              const accessToken = authorizationHeader.replace('Bearer ', '');
              getSignalRConnector().refreshAccessToken(accessToken);
            },
          );

          setGlobalState('ready');
        }}
      />

      {globalState === 'definitivelyDisconnected' && <ExpiredSessionModal />}

      {globalState === 'ready' && (
        <WaitAuthorizedUserBySgConnect onSgConnectError={sgConnect.onError}>
          <FxAccessChecker>
            {children}
            {sgAnalytics !== undefined && <SgwtWebAnalytics siteId={sgAnalytics.siteId} baseUrl={sgAnalytics.url} />}

            {sgSplashScreen !== undefined && <SgwtSplashScreen applicationId={sgSplashScreen.applicationId} />}

            <SgwtHelpCenter
              applicationId={import.meta.env.VITE_APP_ID}
              mailSubject={getConfig().app.helpCenter.subject}
              default-send-to={getConfig().app.helpCenter.contact}
              additionalMessageInformation={{ 'Front version': getConfig().frontVersion }}
              // chatbotConfiguration={{ adapter: 'custom' }}
              // chatbotConfiguration={{ adapter: 'dx-assistant', knowledgeSpace: 'dx', scopes: ['sgmarkets'] }}
            />
          </FxAccessChecker>
        </WaitAuthorizedUserBySgConnect>
      )}
    </SgwtWidgetContextProvider>
  );
};

//  █████╗ ██████╗ ██████╗
// ██╔══██╗██╔══██╗██╔══██╗
// ███████║██████╔╝██████╔╝
// ██╔══██║██╔═══╝ ██╔═══╝
// ██║  ██║██║     ██║
// ╚═╝  ╚═╝╚═╝     ╚═╝

type AuthorizedAppProps = {
  onSgConnectError?: (error: SGWTConnectError) => ReactNode;
};

function onSgConnectReady(userEmail: string | undefined, authorizationToken: string | undefined) {
  if (userEmail === undefined || authorizationToken === undefined) {
    // This is by definition, not possible. If SgConnect is ready, we have a token and a sub.
    logger.logError(
      'SgwtBootstrap: Missing user email or authorization token on SG Connect ready {email_s}',
      userEmail,
    );
    return;
  }
  startWorker(userEmail, authorizationToken);
}

/**
 * display the wrapped application passed as children after SG connect is ready and the user is authorized to connect
 */
const WaitAuthorizedUserBySgConnect = (props: PropsWithChildren<AuthorizedAppProps>) => {
  const { onSgConnectError, children } = props;
  // Get sgwtConnect instance from context
  const { sgwtConnect } = useSgwtWidgets();

  // State to store any authorization error
  const [authorizationError, setAuthorizationError] = useState<SGWTConnectError | null>(null);

  // State to store user claims
  const [claims, setClaims] = useState<any>(null);

  // isAuthorized() just reads the current authorization state of the user
  // it is a getter, so it does not trigger any side effect
  const isAuthorized = sgwtConnect?.sgwtConnect?.isAuthorized() ?? false;

  const authorizationRef = useRef(false);

  useEffect(() => {
    // Wait for sgwtConnect to be available
    if (isNotDefined(sgwtConnect?.sgwtConnect)) {
      return;
    }

    // If user is not authorized, check for error or request authorization
    if (!isAuthorized) {
      const error = sgwtConnect.sgwtConnect.getAuthorizationError();

      if (error == null) {
        // No error: trigger the authorization request
        sgwtConnect.sgwtConnect.requestAuthorization();
      } else {
        // Set the authorization error in state
        setAuthorizationError(error);
      }
    } else {
      // If authorized, get user claims and store email in sessionStorage
      const idTokenClaims = sgwtConnect.sgwtConnect.getIdTokenClaims();

      setClaims(idTokenClaims);

      const email = idTokenClaims?.sub;
      const authorizationToken = getAuthorizationToken();

      if (!authorizationRef.current) {
        authorizationRef.current = true;
        onSgConnectReady(email, authorizationToken);
      }

      if (email) {
        sessionStorage.setItem('userEmail', email);
      }
    }
  }, [sgwtConnect, isAuthorized]);

  // Show a loader while sgwtConnect is not ready
  if (isNotDefined(sgwtConnect?.sgwtConnect)) {
    return <ServiceLoader />;
  }

  // If there is an authorization error and a handler, render the error
  if (authorizationError && onSgConnectError) {
    return onSgConnectError(authorizationError);
  }

  // If authorized and user claims are present, render children
  return claims?.sub ? children : null;
};

const FxAccessChecker = ({ children }: PropsWithChildren) => {
  const [canUseFx, setCanUseFx] = useState<boolean | null>(null);

  useEffect(() => {
    logger.logInformation('expecting user info to decide whether user can use fx...');
    window.SGWTWidgetConfiguration.bus.subscribe('sg-connect.user-info', (userInfo: unknown) => {
      if (isNotDefined(userInfo)) {
        // possible first userInfo message before we get usable information
        logger.logInformation('user info is undefined, ignoring sg-connect.user-info.');
        return;
      }
      const hasAccess = userCanAccessToFx(userInfo);
      logger.logInformation('user info received ! has access to fx: {hasAccess_b}', hasAccess);
      setCanUseFx(hasAccess);
    });
  }, []);

  if (canUseFx === false) {
    logger.logWarning('User has no access to FX, redirecting to access request page...');
    window.location.replace(window.sgmeConfiguration.accessRequestUrl + location.href);
    return null;
  }

  return children;
};
